const DEFAULT_SW_ACTIVATION_TIMEOUT_MS = 5000

interface RecoverToLatestBuildOptions {
  reloadParam: string
  logPrefix: string
  swActivationTimeoutMs?: number
}

export function buildReloadUrl(
  currentUrl: string,
  reloadParam: string,
  markerValue: number | string,
) {
  const nextUrl = new URL(currentUrl)
  nextUrl.searchParams.set(reloadParam, String(markerValue))
  return nextUrl.toString()
}

async function clearAllCaches(logPrefix: string) {
  if (!('caches' in window)) return

  try {
    const keys = await caches.keys()
    await Promise.all(keys.map(key => caches.delete(key)))
    console.log(`[${logPrefix}] All caches cleared`)
  } catch (error) {
    console.error(`[${logPrefix}] Failed to clear caches:`, error)
  }
}

async function forceServiceWorkerUpdate(logPrefix: string) {
  if (!('serviceWorker' in navigator)) return

  try {
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      await registration.update()
      console.log(`[${logPrefix}] SW update triggered`)
    }
  } catch (error) {
    console.error(`[${logPrefix}] SW update failed:`, error)
  }
}

async function waitForSwActivation(logPrefix: string, timeoutMs: number): Promise<void> {
  if (!('serviceWorker' in navigator)) return

  return new Promise(resolve => {
    let settled = false
    let timeoutId: number | undefined

    const finish = (message: string) => {
      if (settled) return
      settled = true
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange)
      console.log(`[${logPrefix}] ${message}`)
      resolve()
    }

    const handleControllerChange = () => {
      finish('New SW controller')
    }

    const checkActivation = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration()
        if (registration?.active?.state === 'activated') {
          finish('SW is active')
          return
        }
      } catch (error) {
        console.error(`[${logPrefix}] SW activation check failed:`, error)
        finish('SW activation check failed, proceeding')
        return
      }

      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
      timeoutId = window.setTimeout(() => {
        finish('SW activation timeout, proceeding')
      }, timeoutMs)
    }

    void checkActivation()
  })
}

export async function recoverToLatestBuild({
  reloadParam,
  logPrefix,
  swActivationTimeoutMs = DEFAULT_SW_ACTIVATION_TIMEOUT_MS,
}: RecoverToLatestBuildOptions) {
  console.log(`[${logPrefix}] Starting reload sequence`)

  await clearAllCaches(logPrefix)
  await forceServiceWorkerUpdate(logPrefix)
  await waitForSwActivation(logPrefix, swActivationTimeoutMs)

  const reloadUrl = buildReloadUrl(window.location.href, reloadParam, Date.now())
  console.log(`[${logPrefix}] Reloading to:`, reloadUrl)
  window.location.replace(reloadUrl)
}
